import React, { useEffect, useRef, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Camera, Phone, Mail, FileText, Upload, GripVertical, Users, CheckCircle, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { Agent } from '../../types/Agent';

import { uploadMedia } from '../../lib/media';
import { useContentStore } from './hooks/useContentStore';

const AgentManager: React.FC = () => {
  const [agentsData, setAgentsData] = useContentStore<any>('agents', []);
  
  // Normalize legacy object-shaped data to an array.
  const agents = Array.isArray(agentsData) ? agentsData : Object.values(agentsData || {});
  const setAgents = (newAgents: Agent[] | ((prev: Agent[]) => Agent[])) => {
    const agentsArray = typeof newAgents === 'function' ? newAgents(agents) : newAgents;
    return setAgentsData(agentsArray);
  };
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploadToast, setUploadToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const uploadToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formData, setFormData] = useState<Partial<Agent>>({
    name: '',
    title: 'Independent Licensed Agent',
    bio: '',
    image: '',
    phone: '',
    email: '',
    planenrollUrl: '',
    specialties: [],
    yearsExperience: 0,
    licenseNumber: ''
  });

  useEffect(() => {
    return () => {
      if (uploadToastTimeoutRef.current) {
        clearTimeout(uploadToastTimeoutRef.current);
      }
    };
  }, []);

  const showUploadToast = (type: 'success' | 'error', message: string) => {
    setUploadToast({ type, message });
    if (uploadToastTimeoutRef.current) {
      clearTimeout(uploadToastTimeoutRef.current);
    }
    uploadToastTimeoutRef.current = setTimeout(() => {
      setUploadToast(null);
    }, type === 'error' ? 5000 : 3000);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setUploadToast(null);
    setFormData({
      name: '',
      title: 'Independent Licensed Agent',
      bio: '',
      image: '',
      phone: '',
      email: '',
      planenrollUrl: '',
      specialties: [],
      yearsExperience: 0,
      licenseNumber: ''
    });
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setUploadToast(null);
    setFormData(agent);
  };

  const handleDelete = (agentId: string) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      setAgents(prev => prev.filter(agent => agent.id !== agentId));
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex !== dropIndex) {
      const newAgents = [...agents];
      const draggedAgent = newAgents[dragIndex];
      newAgents.splice(dragIndex, 1);
      newAgents.splice(dropIndex, 0, draggedAgent);
      setAgents(newAgents);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError('');
    try {
    if (isAddingNew) {
      const newAgent: Agent = {
        id: Date.now().toString(),
        name: formData.name || '',
        title: formData.title || 'Independent Licensed Agent',
        bio: formData.bio || '',
        image: formData.image || '/bhba-logo.png',
        phone: formData.phone || '',
        email: formData.email || '',
        planenrollUrl: formData.planenrollUrl || '',
        websiteUrl: formData.websiteUrl || '',
        specialties: formData.specialties || [],
        yearsExperience: formData.yearsExperience || 0,
        licenseNumber: formData.licenseNumber || ''
      };
      await setAgents(prev => [...prev, newAgent]);
      setIsAddingNew(false);
    } else if (editingAgent) {
      await setAgents(prev => prev.map(agent =>
        agent.id === editingAgent.id ? { ...agent, ...formData } : agent
      ));
      setEditingAgent(null);
    }
    setUploadToast(null);
    setFormData({});
    } catch {
      setSaveError('We could not save this team member. Please try again or contact Kyle at kylemirich20@gmail.com.');
    } finally {
      setIsSaving(false);
    }
  };

  const moveAgent = async (index: number, direction: number) => {
    const reordered = [...agents];
    const target = index + direction;
    if (target < 0 || target >= reordered.length) return;
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setIsSaving(true);
    setSaveError('');
    try { await setAgents(reordered); }
    catch { setSaveError('We could not save the new order. Please try again or contact Kyle at kylemirich20@gmail.com.'); }
    finally { setIsSaving(false); }
  };

  const handleCancel = () => {
    setEditingAgent(null);
    setIsAddingNew(false);
    setUploadToast(null);
    setFormData({});
  };

  const handleInputChange = (field: keyof Agent, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtiesChange = (specialtiesText: string) => {
    const specialties = specialtiesText.split(',').map(s => s.trim()).filter(s => s.length > 0);
    handleInputChange('specialties', specialties);
  };

  if (isAddingNew || editingAgent) {
    return (
      <div className="min-w-0 p-4 sm:p-6">
        {uploadToast && (
          <div
            className={`fixed top-6 right-6 z-50 flex max-w-sm items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${
              uploadToast.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
            role={uploadToast.type === 'success' ? 'status' : 'alert'}
            aria-live="polite"
          >
            {uploadToast.type === 'success' ? (
              <CheckCircle size={20} className="flex-shrink-0 text-green-600" />
            ) : (
              <AlertCircle size={20} className="flex-shrink-0 text-red-600" />
            )}
            <span className="font-medium">{uploadToast.message}</span>
          </div>
        )}
        <div className="mb-6 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            {isAddingNew ? 'Add Team Member' : 'Edit Team Member'}
          </h1>
          <button
            onClick={handleCancel}
            className="flex min-h-11 items-center gap-2 self-start text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <X size={20} />
            <span>Cancel</span>
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="max-w-4xl">
          {saveError && <p role="alert" className="text-red-700 mb-4">{saveError}</p>}
          <div className="grid min-w-0 gap-6 lg:grid-cols-3 lg:gap-8">
            {/* Left Column - Photo */}
            <div className="space-y-4">
              <div className="aspect-[5/4] bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center">
                {formData.image ? (
                  <img 
                    src={formData.image} 
                    alt={formData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-gray-500">
                    <Camera size={48} className="mx-auto mb-2" />
                    <p>No photo uploaded</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Photo
                </label>
                <label className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Upload size={16} className="mr-2" />
                  <span>{isUploading ? 'Preparing photo…' : 'Upload Photo'}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    aria-label="Upload Photo"
                    disabled={isUploading}
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setIsUploading(true);
                        try {
                          const url = await uploadMedia(file);
                          handleInputChange('image', url);
                          showUploadToast('success', 'The photo is ready to use.');
                        } catch {
                          showUploadToast('error', 'We could not upload this photo. Try a smaller image or contact Kyle at kylemirich20@gmail.com.');
                        } finally {
                          setIsUploading(false);
                        }
                      }
                    }}
                  />
                </label>

              </div>
            </div>

            {/* Right Columns - Form */}
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  value={formData.bio || ''}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address (optional)
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Enroll Link
                </label>
                <input
                  type="url"
                  value={formData.planenrollUrl || ''}
                  onChange={(e) => handleInputChange('planenrollUrl', e.target.value)}
                  placeholder="https://www.planenroll.com/medicare/products?purl=Agent-Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="agent-website" className="block text-sm font-medium text-gray-700 mb-2">Website Link (optional)</label>
                <input id="agent-website" type="url" pattern="https?://.*" value={formData.websiteUrl || ''}
                  onChange={(e) => handleInputChange('websiteUrl', e.target.value)} placeholder="https://example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>

              <div>
                <label htmlFor="agent-experience" className="block text-sm font-medium text-gray-700 mb-2">Years of Experience (optional)</label>
                <input id="agent-experience" type="number" min="0" step="1" value={formData.yearsExperience || ''}
                  onChange={(e) => handleInputChange('yearsExperience', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Number
                </label>
                <input
                  type="text"
                  value={formData.licenseNumber || ''}
                  onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                  placeholder="CA LIC# 0000000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specialties (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.specialties?.join(', ') || ''}
                  onChange={(e) => handleSpecialtiesChange(e.target.value)}
                  placeholder="Medicare Advantage, Supplement Plans, Medicare Guidance"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid gap-3 border-t border-gray-200 pt-6 sm:flex sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || isUploading}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  <Save size={16} />
                  <span>{isAddingNew ? 'Add Team Member' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-w-0 p-4 sm:p-6">
      {saveError && <p role="alert" className="text-red-700 mb-4">{saveError}</p>}
      <div className="mb-6 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Team Members</h1>
          <p className="text-gray-600 mt-1">Add or update the people shown on your website</p>
        </div>
        
        <div className="flex items-center space-x-4">

          
          <button
            onClick={handleAddNew}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700 sm:w-auto"
          >
            <Plus size={16} />
            <span>Add Team Member</span>
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        {Array.isArray(agents) && agents.map((agent, index) => (
          <div 
            key={agent.id} 
            className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 transition-shadow duration-200 hover:shadow-lg sm:p-6"
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              {/* Drag Handle */}
              <div className="hidden shrink-0 cursor-move pt-2 text-gray-400 hover:text-gray-600 sm:block">
                <GripVertical size={20} />
              </div>
              
              {/* Agent Photo */}
              <div className="flex-shrink-0">
                <img
                  src={agent.image}
                  alt={agent.name}
                  className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200"
                />
              </div>

              {/* Agent Info */}
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-gray-900">{agent.name}</h3>
                    <p className="text-blue-600 font-medium">{agent.title}</p>
                    <p className="mt-2 line-clamp-3 max-w-2xl break-words text-sm text-gray-600 sm:line-clamp-none sm:text-base">{agent.bio}</p>
                  </div>
                  
                  <div className="flex shrink-0 flex-wrap items-center gap-1">
                    <button type="button" aria-label={`Move ${agent.name} up`} disabled={isSaving || index === 0}
                      onClick={() => moveAgent(index, -1)} className="min-h-11 min-w-11 p-3 text-gray-600 disabled:opacity-30"><ArrowUp size={18} /></button>
                    <button type="button" aria-label={`Move ${agent.name} down`} disabled={isSaving || index === agents.length - 1}
                      onClick={() => moveAgent(index, 1)} className="min-h-11 min-w-11 p-3 text-gray-600 disabled:opacity-30"><ArrowDown size={18} /></button>
                    <button
                      onClick={() => handleEdit(agent)}
                      className="min-h-11 min-w-11 rounded-lg p-3 text-blue-600 transition-colors duration-200 hover:bg-blue-50"
                      title="Edit Agent"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(agent.id)}
                      className="min-h-11 min-w-11 rounded-lg p-3 text-red-600 transition-colors duration-200 hover:bg-red-50"
                      title="Delete Agent"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div className="flex min-w-0 items-center gap-2 break-all text-gray-600">
                    <Phone size={16} />
                    <span>{agent.phone}</span>
                  </div>
                  <div className="flex min-w-0 items-center gap-2 break-all text-gray-600">
                    <Mail size={16} />
                    <span>{agent.email}</span>
                  </div>
                  {agent.planenrollUrl && (
                    <div className="flex min-w-0 items-center gap-2 break-all text-gray-600">
                      <FileText size={16} />
                      <span>Plan Enroll</span>
                    </div>
                  )}
                </div>

                {/* Specialties and License */}
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div className="text-sm text-gray-600">
                    {agent.licenseNumber}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {agent.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="max-w-full break-words rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {Array.isArray(agents) && agents.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Users size={64} className="mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-600 mb-2">No team members yet</h3>
          <p className="text-gray-500 mb-6">Add the first person you would like to show on the website.</p>
          <button
            onClick={handleAddNew}
            className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 mx-auto"
          >
            <Plus size={16} />
            <span>Add First Team Member</span>
          </button>
        </div>
      )}


    </div>
  );
};

export default AgentManager;
